import { useLazyQuery } from '@apollo/client';
import { Box } from '@ovComponents/1-primative/box/box';
import { Skeleton } from '@ovComponents/1-primative/skeleton/skeleton';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { TableCell, TableRow } from '@ovComponents/2-component/table/table';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { UserContext } from '@providers/userContextProvider';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { useContext, useEffect, useMemo } from 'react';
import {
  FETCH_TRADE_ROW_PIPELINE,
  FETCH_TRADE_ROW_POSITION,
  FETCH_TRADE_ROW_SETTLED,
} from '../queries';
import { BORDERED_BOX_SX, SECTION_HEADER_SX, SMALL_SX } from '../styles';
import { isBuyType } from '../utils';

const STATE_LABELS: Record<string, { label: string; color?: string }> = {
  INITIATED: { label: 'Staged' },
  READY: { label: 'Ready' },
  REQUESTED: { label: 'Sent' },
  RECONCILED: { label: 'Filled' },
  CANCELED: { label: 'Canceled' },
};

const ContextCard = ({
  title,
  loading,
  children,
  sys,
  accent,
}: {
  title: string;
  loading: boolean;
  children: React.ReactNode;
  sys: any;
  accent?: string;
}) => (
  <Box
    flex={1}
    sx={{
      border: '1px solid',
      borderColor: sys.color.outlineVariant,
      borderLeft: '3px solid',
      borderLeftColor: accent || sys.color.primary,
      borderRadius: 1,
      bgcolor: sys.color.surface,
      p: 1.5,
      minHeight: 110,
    }}
  >
    <Typography
      variant='labelSmall'
      sx={{
        ...SECTION_HEADER_SX,
        mb: 1,
        color: accent || sys.color.primary,
      }}
    >
      {title}
    </Typography>
    {loading ? (
      <Box display='flex' flexDirection='column' gap={0.75}>
        <Skeleton width='80%' height={16} />
        <Skeleton width='55%' height={16} />
        <Skeleton width='70%' height={16} />
      </Box>
    ) : (
      children
    )}
  </Box>
);

const MiniRow = ({
  cells,
  sys,
  header,
}: {
  cells: Array<{
    value: string;
    align?: 'right';
    bold?: boolean;
    color?: string;
  }>;
  sys: any;
  header?: boolean;
}) => (
  <Box
    display='flex'
    gap={1}
    py={header ? 0.5 : 0.375}
    px={0.75}
    sx={{
      borderBottom: '1px solid',
      borderColor: header ? sys.color.outline : sys.color.outlineVariant,
      ...(header
        ? { bgcolor: sys.color.neutralHover }
        : { '&:hover': { bgcolor: sys.color.neutralHover } }),
    }}
  >
    {cells.map((cell, i) => (
      <Typography
        key={i}
        variant='labelSmall'
        sx={{
          fontSize: header ? '0.6rem' : '0.7rem',
          flex: i === 0 ? '0 0 55px' : 1,
          textAlign: cell.align || 'left',
          fontWeight: cell.bold ? 700 : 400,
          letterSpacing: header ? '0.03em' : 0,
          ...(cell.color
            ? { color: cell.color }
            : {
                color: header
                  ? sys.color.onSurfaceVariant
                  : sys.color.onSurface,
              }),
        }}
      >
        {cell.value}
      </Typography>
    ))}
  </Box>
);

const MetricBlock = ({
  label,
  value,
  sub,
  sys,
  color,
}: {
  label: string;
  value: string;
  sub?: string;
  sys: any;
  color?: string;
}) => (
  <Box mb={0.75}>
    <Typography
      variant='labelSmall'
      sx={{
        ...SMALL_SX,
        color: sys.color.onSurfaceVariant,
        letterSpacing: '0.03em',
        lineHeight: 1,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant='bodySmall'
      weight='bold'
      sx={{
        fontSize: '0.9rem',
        lineHeight: 1.3,
        color: color || sys.color.onSurface,
      }}
    >
      {value}
    </Typography>
    {sub && (
      <Typography
        variant='labelSmall'
        sx={{ ...SMALL_SX, color: sys.color.onSurfaceVariant }}
      >
        {sub}
      </Typography>
    )}
  </Box>
);

const EmptyHint = ({ text, sys }: { text: string; sys: any }) => (
  <Box
    display='flex'
    alignItems='center'
    justifyContent='center'
    sx={{ minHeight: 60, opacity: 0.6 }}
  >
    <Typography
      variant='bodySmall'
      sx={{
        color: sys.color.onSurfaceVariant,
        fontStyle: 'italic',
        textAlign: 'center',
      }}
    >
      {text}
    </Typography>
  </Box>
);

type TradeRowDetailProps = {
  trade: any;
  colSpan: number;
  stateInfo: { label: string; description: string };
  isBuy: boolean;
  isBlocked: boolean;
};

const TradeRowDetail = ({
  trade,
  colSpan,
  stateInfo,
  isBuy,
  isBlocked,
}: TradeRowDetailProps) => {
  const { sys } = useThemeTokens();
  const { activeOrganization } = useContext(UserContext);
  const requireVerification =
    !!activeOrganization?.requireSubTradeRequestApproval;

  const subAccountId = trade.subAccount?.id;
  const goalId = trade.subAccount?.goal?.id;
  const financialProductId = trade.financialProduct?.id;
  const organizationId = activeOrganization?.id;

  const [fetchPosition, { data: posData, loading: posLoading }] = useLazyQuery(
    FETCH_TRADE_ROW_POSITION,
    { fetchPolicy: 'cache-first' },
  );
  const [fetchPipeline, { data: pipeData, loading: pipeLoading }] =
    useLazyQuery(FETCH_TRADE_ROW_PIPELINE, { fetchPolicy: 'cache-first' });
  const [fetchSettled, { data: settledData, loading: settledLoading }] =
    useLazyQuery(FETCH_TRADE_ROW_SETTLED, { fetchPolicy: 'cache-first' });

  useEffect(() => {
    if (!subAccountId || !organizationId) return;
    const positionFilter = goalId
      ? { goalId, organizationId }
      : { subAccountId, organizationId };
    fetchPosition({
      variables: {
        input: {
          filter: positionFilter,
          pagination: { perPage: 1, offSet: 0 },
        },
      },
    });
    if (financialProductId) {
      fetchPipeline({
        variables: {
          input: {
            filter: { subAccountId, financialProductId },
            pagination: {
              sortField: 'createdAt',
              sortDesc: true,
              perPage: 6,
              offSet: 0,
            },
          },
        },
      });
      fetchSettled({
        variables: {
          input: {
            filter: {
              subAccountIds: [subAccountId],
              financialProductIds: [financialProductId],
              types: ['BUY', 'SELL'],
            },
            pagination: {
              sortField: 'date',
              sortDesc: true,
              perPage: 5,
              offSet: 0,
            },
          },
        },
      });
    }
  }, [
    subAccountId,
    goalId,
    organizationId,
    financialProductId,
    fetchPosition,
    fetchPipeline,
    fetchSettled,
  ]);

  const holding = useMemo(() => {
    const reports = posData?.fetchPortfolioReports?.portfolioReports;
    if (!reports?.length || !financialProductId) return null;
    const report = reports[0];
    const h = report.holdings?.find(
      (h: any) => h.financialProduct?.id === financialProductId,
    );
    if (!h) return null;

    let cashAvailableCents = report.cashAvailableForTradeCents ?? 0;
    let subAccountMarketValueCents = report.marketValueCents ?? 0;

    if (goalId && subAccountId && report.subAccounts?.length) {
      const sa = report.subAccounts.find(
        (s: any) => s.subAccount?.id === subAccountId,
      );
      if (sa) {
        cashAvailableCents =
          sa.cashAvailableForTradeCents ?? cashAvailableCents;
        subAccountMarketValueCents =
          sa.marketValueCents ?? subAccountMarketValueCents;
      }
    }

    return {
      quantity: h.quantity ?? 0,
      valueCents: h.valueCents ?? 0,
      pctOfPortfolio: h.expectedPercentage ?? 0,
      portfolioMarketValueCents: subAccountMarketValueCents,
      cashAvailableCents,
    };
  }, [posData, financialProductId, goalId, subAccountId]);

  const pipelineTrades = useMemo(() => {
    const strs = pipeData?.fetchSubTradeRequests?.subTradeRequests || [];
    return strs.filter((s: any) => s.id !== trade.id).slice(0, 5);
  }, [pipeData, trade.id]);

  const settledTxns = useMemo(
    () => settledData?.fetchTransactions?.transactions || [],
    [settledData],
  );

  return (
    <TableRow>
      <TableCell colSpan={colSpan}>
        <Box
          p={2}
          sx={{
            bgcolor: sys.color.surface,
            ...BORDERED_BOX_SX,
            borderRadius: 1.5,
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          }}
        >
          {/* Compact status + flags strip */}
          <Box
            display='flex'
            alignItems='center'
            flexWrap='wrap'
            gap={1.5}
            mb={1.5}
            pb={1.5}
            sx={{
              borderBottom: '1px solid',
              borderColor: sys.color.outlineVariant,
            }}
          >
            <Typography
              variant='bodySmall'
              sx={{ fontStyle: 'italic', color: sys.color.onSurfaceVariant }}
            >
              {stateInfo.description}
            </Typography>
            {requireVerification &&
              (trade.verifiedAt ? (
                <Typography
                  variant='labelSmall'
                  sx={{ ...SMALL_SX, color: sys.color.positive }}
                >
                  Verified{' '}
                  {trade.verifiedByOrganizationUser
                    ? `by ${trade.verifiedByOrganizationUser.firstName} ${trade.verifiedByOrganizationUser.lastName}`
                    : ''}{' '}
                  — {new Date(trade.verifiedAt).toLocaleString()}
                </Typography>
              ) : (
                <Typography
                  variant='labelSmall'
                  sx={{ ...SMALL_SX, color: sys.color.warning }}
                >
                  Not verified
                </Typography>
              ))}
            {trade.isSellAll && (
              <Typography
                variant='labelSmall'
                sx={{ ...SMALL_SX, color: sys.color.warning, fontWeight: 700 }}
              >
                SELL ALL
              </Typography>
            )}
            {trade.bulkTradeRun && (
              <Typography
                variant='labelSmall'
                sx={{ ...SMALL_SX, color: sys.color.primary }}
              >
                In Batch
              </Typography>
            )}
            {trade.pair && (
              <Typography variant='labelSmall' sx={{ ...SMALL_SX }}>
                Paired with {trade.pair.financialProduct?.ticker || '?'}
              </Typography>
            )}
            {trade.blockedUntilAccountActive && (
              <Typography
                variant='labelSmall'
                sx={{ ...SMALL_SX, color: sys.color.negative, fontWeight: 700 }}
              >
                Blocked — Account Inactive
              </Typography>
            )}
            {trade.blockedUntilSubAccountActive && (
              <Typography
                variant='labelSmall'
                sx={{ ...SMALL_SX, color: sys.color.negative, fontWeight: 700 }}
              >
                Blocked — Sub-Account Inactive
              </Typography>
            )}
            {trade.blockedUntil && (
              <Typography
                variant='labelSmall'
                sx={{ ...SMALL_SX, color: sys.color.warning }}
              >
                Blocked until {new Date(trade.blockedUntil).toLocaleString()}
              </Typography>
            )}
            {trade.schedule && (
              <Typography
                variant='labelSmall'
                sx={{ ...SMALL_SX, color: sys.color.warning }}
              >
                Schedule: {trade.schedule.name}
              </Typography>
            )}
            {isBuy &&
              trade.state === 'INITIATED' &&
              !isBlocked &&
              !trade.bulkTradeRun && (
                <Typography
                  variant='labelSmall'
                  sx={{ ...SMALL_SX, color: sys.color.warning }}
                >
                  Likely insufficient cash — unable to fund this BUY
                </Typography>
              )}
          </Box>

          {/* Lazy-loaded context cards */}
          <Box display='flex' gap={2}>
            {/* Current Position */}
            <ContextCard
              title='CURRENT POSITION'
              loading={posLoading}
              sys={sys}
              accent={sys.color.primary}
            >
              {holding ? (
                <Box display='flex' flexDirection='column'>
                  <Box display='flex' gap={2} mb={0.5}>
                    <MetricBlock
                      label='SHARES'
                      value={holding.quantity.toFixed(4)}
                      sys={sys}
                    />
                    <MetricBlock
                      label='MARKET VALUE'
                      value={formatMoneyValue(holding.valueCents, '$', 2)}
                      sys={sys}
                    />
                  </Box>
                  <Box display='flex' gap={2}>
                    <MetricBlock
                      label='% OF PORTFOLIO'
                      value={`${(holding.pctOfPortfolio * 100).toFixed(1)}%`}
                      sys={sys}
                    />
                    <MetricBlock
                      label='CASH AVAILABLE'
                      value={formatMoneyValue(
                        holding.cashAvailableCents,
                        '$',
                        2,
                      )}
                      sys={sys}
                      color={sys.color.positive}
                    />
                  </Box>
                </Box>
              ) : (
                <EmptyHint text='No position data available' sys={sys} />
              )}
            </ContextCard>

            {/* Pipeline Activity */}
            <ContextCard
              title='PIPELINE ACTIVITY'
              loading={pipeLoading}
              sys={sys}
              accent={sys.color.warning}
            >
              {pipelineTrades.length > 0 ? (
                <Box display='flex' flexDirection='column'>
                  <MiniRow
                    sys={sys}
                    header
                    cells={[
                      { value: 'DATE', bold: true },
                      { value: 'SIDE', bold: true },
                      { value: 'AMOUNT', align: 'right', bold: true },
                      { value: 'STATUS', bold: true },
                    ]}
                  />
                  {pipelineTrades.map((s: any) => {
                    const sl = STATE_LABELS[s.state] || { label: s.state };
                    const buy = isBuyType(s.type);
                    return (
                      <MiniRow
                        key={s.id}
                        sys={sys}
                        cells={[
                          {
                            value: s.createdAt
                              ? new Date(s.createdAt).toLocaleDateString(
                                  undefined,
                                  { month: 'numeric', day: 'numeric' },
                                )
                              : '—',
                          },
                          {
                            value: buy ? 'BUY' : 'SELL',
                            color: buy
                              ? sys.color.positive
                              : sys.color.negative,
                            bold: true,
                          },
                          {
                            value: formatMoneyValue(
                              Math.abs(s.moneyAllocatedCents || 0),
                              '$',
                              2,
                            ),
                            align: 'right',
                          },
                          { value: sl.label },
                        ]}
                      />
                    );
                  })}
                </Box>
              ) : (
                <EmptyHint
                  text='No other pipeline trades for this security'
                  sys={sys}
                />
              )}
            </ContextCard>

            {/* Settled History */}
            <ContextCard
              title='SETTLED HISTORY'
              loading={settledLoading}
              sys={sys}
              accent={sys.color.positive}
            >
              {settledTxns.length > 0 ? (
                <Box display='flex' flexDirection='column'>
                  <MiniRow
                    sys={sys}
                    header
                    cells={[
                      { value: 'DATE', bold: true },
                      { value: 'SIDE', bold: true },
                      { value: 'QTY', align: 'right', bold: true },
                      { value: 'PRICE', align: 'right', bold: true },
                      { value: 'VALUE', align: 'right', bold: true },
                    ]}
                  />
                  {settledTxns.map((tx: any) => {
                    const txBuy = tx.type === 'BUY';
                    return (
                      <MiniRow
                        key={tx.id}
                        sys={sys}
                        cells={[
                          {
                            value: tx.date
                              ? new Date(tx.date).toLocaleDateString(
                                  undefined,
                                  { month: 'numeric', day: 'numeric' },
                                )
                              : '—',
                          },
                          {
                            value: txBuy ? 'BUY' : 'SELL',
                            color: txBuy
                              ? sys.color.positive
                              : sys.color.negative,
                            bold: true,
                          },
                          {
                            value: (tx.quantity ?? 0).toFixed(2),
                            align: 'right',
                          },
                          {
                            value: tx.priceCents
                              ? formatMoneyValue(tx.priceCents, '$', 2)
                              : '—',
                            align: 'right',
                          },
                          {
                            value: formatMoneyValue(
                              Math.abs(tx.valueCents || 0),
                              '$',
                              2,
                            ),
                            align: 'right',
                            bold: true,
                          },
                        ]}
                      />
                    );
                  })}
                </Box>
              ) : (
                <EmptyHint text='No settled trades found' sys={sys} />
              )}
            </ContextCard>
          </Box>
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default TradeRowDetail;
